/**
 * SVG path parsing for country boundaries
 *
 * Ported from Python (map_projection/svg_model.py lines 255-296)
 * Parses world.svg to extract country boundaries and convert relative
 * SVG coordinates to absolute coordinates.
 */

/**
 * Parse SVG path 'd' attribute to absolute coordinates
 * Handles relative coordinate accumulation and multiple subpaths
 *
 * @param {string} pathData - SVG path data string (e.g., "m 10,20 30,40 z")
 * @returns {Array<Array<[number, number]>>} Array of subpaths, each is array of [x,y] coords
 */
export function parseSVGPathData(pathData) {
    // Split on 'z' (case-insensitive) to get subpaths
    const subpaths = pathData.trim().split(/z/i).filter(s => s.trim());

    const allPaths = [];
    let x = 0, y = 0; // Accumulator for relative coords

    for (const subpath of subpaths) {
        // Remove 'm' command and commas
        const clean = subpath.replace(/m/i, '').replace(/,/g, ' ');

        // Extract numbers (including scientific notation: 10e-4)
        const numbers = clean.match(/[-+]?\d*\.?\d+(?:e[-+]?\d+)?/gi);

        if (!numbers || numbers.length === 0) continue;

        const floats = numbers.map(Number);

        // Group into [x, y] pairs and accumulate to absolute coords
        const coords = [];
        for (let i = 0; i < floats.length; i += 2) {
            if (i + 1 >= floats.length) break; // Need pairs

            x += floats[i];      // Accumulate relative offsets
            y += floats[i + 1];
            coords.push([x, y]);
        }

        // Auto-close path (duplicate first coordinate)
        if (coords.length > 0) {
            coords.push([coords[0][0], coords[0][1]]);
            allPaths.push(coords);
        }
    }

    return allPaths;
}

/**
 * Convert HSV color to RGB
 *
 * @param {number} h - Hue [0, 1]
 * @param {number} s - Saturation [0, 1]
 * @param {number} v - Value/Brightness [0, 1]
 * @returns {Array<number>} RGB [0-255]
 */
function hsvToRgb(h, s, v) {
    const i = Math.floor(h * 6);
    const f = h * 6 - i;
    const p = v * (1 - s);
    const q = v * (1 - f * s);
    const t = v * (1 - (1 - f) * s);

    let r, g, b;
    switch (i % 6) {
        case 0: [r, g, b] = [v, t, p]; break;
        case 1: [r, g, b] = [q, v, p]; break;
        case 2: [r, g, b] = [p, v, t]; break;
        case 3: [r, g, b] = [p, q, v]; break;
        case 4: [r, g, b] = [t, p, v]; break;
        case 5: [r, g, b] = [v, p, q]; break;
    }

    return [Math.round(r * 255), Math.round(g * 255), Math.round(b * 255)];
}

/**
 * Calculate Euclidean distance between two RGB colors
 *
 * @param {Array<number>} c1 - RGB color 1 [0-255]
 * @param {Array<number>} c2 - RGB color 2 [0-255]
 * @returns {number} Euclidean distance
 */
function colorDistance(c1, c2) {
    return Math.sqrt(
        (c1[0] - c2[0]) ** 2 +
        (c1[1] - c2[1]) ** 2 +
        (c1[2] - c2[2]) ** 2
    );
}

/**
 * Simple string hash function (fallback for non-secure contexts)
 *
 * @param {string} str - String to hash
 * @returns {number} Hash value
 */
function simpleHash(str) {
    let hash = 0;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32-bit integer
    }
    return Math.abs(hash);
}

/**
 * Generate deterministic color from country name and an integer seed.
 * seed=0 produces the default palette; each increment yields a distinct palette.
 *
 * @param {string} name - Country name
 * @param {number} seed - Integer palette index (0-based)
 * @param {Array<number>} backgroundColor - RGB background [0-255]
 * @returns {Array<number>} RGB color [0-255]
 */
export function countryColor(name, seed = 0, backgroundColor = [3, 165, 252]) {
    const hash = simpleHash(name);
    let hue = ((hash % 65536) / 65536.0 + seed / 10) % 1.0;

    const saturation = 0.9;
    const value = 0.9;

    let rgb = hsvToRgb(hue, saturation, value);

    if (colorDistance(rgb, backgroundColor) < 80) {
        hue = (hue + 0.5) % 1.0;
        rgb = hsvToRgb(hue, saturation, value);
    }

    return rgb;
}

/**
 * Parse SVG document to extract country paths
 *
 * @param {Document} svgDoc - Parsed SVG document
 * @returns {Promise<Object>} Country data with 2D coordinates
 */
export function parseSVGDocument(svgDoc) {
    const svg = svgDoc.querySelector('svg');
    const width = parseFloat(svg.getAttribute('width'));
    let height = parseFloat(svg.getAttribute('height'));

    // NOTE: Estimate extra y-axis missing on the map (polar regions)
    // This adjustment ensures correct latitude mapping when converting to sphere coordinates
    // Ported from Python: svg_model.py lines 252-253
    height *= 1.39;

    const pathElements = svgDoc.querySelectorAll('path');

    const countries = new Map();

    console.log(`Parsing ${pathElements.length} countries from SVG...`);

    for (const path of pathElements) {
        const id = path.getAttribute('id');
        const title = path.getAttribute('title');
        const d = path.getAttribute('d');

        if (!id || !title || !d) {
            console.warn(`Skipping path without id/title/d:`, path);
            continue;
        }

        // Parse SVG path data to absolute coordinates
        const subpaths = parseSVGPathData(d);

        // Generate deterministic color for this country
        const color = countryColor(title, 0);

        // Convert coordinate arrays to Float32Array for performance
        const paths = subpaths.map(coords => ({
            svg2D: new Float32Array(coords.flat()),
            vertexCount: coords.length
        }));

        countries.set(id, {
            id,
            name: title,
            color,
            paths
        });
    }

    console.log(`Parsed ${countries.size} countries`);

    return { countries, width, height };
}

/**
 * Load and parse SVG file
 *
 * @param {string} path - Path to SVG file (e.g., '/assets/world.svg')
 * @returns {Promise<Object>} Parsed SVG data with country information
 */
export async function loadSVG(path) {
    console.log(`Loading SVG from ${path}...`);

    const response = await fetch(path);
    if (!response.ok) {
        throw new Error(`Failed to load SVG: ${response.status} ${response.statusText}`);
    }

    const svgText = await response.text();
    const parser = new DOMParser();
    const svgDoc = parser.parseFromString(svgText, 'image/svg+xml');

    // Check for parse errors
    const parseError = svgDoc.querySelector('parsererror');
    if (parseError) {
        throw new Error(`SVG parse error: ${parseError.textContent}`);
    }

    return parseSVGDocument(svgDoc);
}
